/**
 * Friends & Referrals screen.
 *
 * Data source: GET /admin/metrics/friends (Phase C).
 * `referral_invites_sent` is spec'd but not tracked yet — the backend
 * returns null with a `_reason` field; we surface "n/a" so it's visible
 * without misleading zeros.
 */

import { api, ApiError } from '../../api.js';
import { toast } from '../../components/toast.js';
import {
  downloadCsv,
  errorState,
  escape,
  fetchOrNull,
  fmtBps,
  fmtInt,
  fmtRelTime,
  kpi,
  mountLineChart,
  mountSortableTable,
  rangeQuery,
  skeletonGrid,
} from './common.js';

export async function renderFriends(host, ctx) {
  host.innerHTML = skeletonGrid(4);
  try {
    // Top-referrers is optional — a slow / broken call on the new
    // endpoint shouldn't take the whole page down. fetchOrNull swallows
    // and we render an empty panel below.
    const [data, referrers] = await Promise.all([
      api(`/admin/metrics/friends${rangeQuery(ctx.range)}`),
      fetchOrNull('/admin/metrics/referrers?limit=25&sort=total&order=desc'),
    ]);
    host.innerHTML = _render(data);
    _mountCharts(host, data);
    _mountReferrersTable(host, ctx, referrers);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      ctx.navigate('/login');
      return;
    }
    toast.fromError(err);
    host.innerHTML = errorState(err);
  }
}

export function exportCsv({ range }) {
  const qs = rangeQuery(range);
  const path = qs ? `/admin/metrics/friends${qs}&format=csv` : '/admin/metrics/friends?format=csv';
  downloadCsv(path, `triviana_friends_${range.start}_${range.end}.csv`);
}

function _render(d) {
  const t = d.totals || {};
  const series = d.timeseries || [];

  const cards = `
    <div class="adm2-grid">
      ${kpi({ label: 'Referrals joined', value: fmtInt(t.referrals_joined), hint: 'this window' })}
      ${kpi({ label: 'Referrals rewarded', value: fmtInt(t.referrals_rewarded), hint: 'triggered payout' })}
      ${kpi({ label: 'Referral conv.', value: fmtBps(t.referral_conversion_bps), hint: 'joined → rewarded' })}
      ${kpi({ label: 'Friend requests sent', value: fmtInt(t.friend_requests_sent), hint: 'window' })}
      ${kpi({ label: 'Friendships accepted', value: fmtInt(t.friendships_accepted), hint: 'window' })}
      ${kpi({ label: 'Accept rate', value: fmtBps(t.friend_request_accept_rate_bps), hint: 'accepted ÷ sent' })}
      ${kpi({
        label: 'Referral invites sent',
        value: t.referral_invites_sent == null ? 'n/a' : fmtInt(t.referral_invites_sent),
        hint: t.referral_invites_sent == null ? 'not tracked yet' : 'window',
      })}
    </div>
  `;

  const chart = `<div id="adm2-friends-chart"></div>`;

  return `
    <div class="adm2-section-label">Referral + friendship activity · windowed</div>
    ${cards}
    <div class="adm2-panel" style="margin-top:18px;">
      <div class="adm2-panel-title">Referrals joined vs friendships accepted</div>
      <div class="adm2-panel-sub">daily/weekly buckets</div>
      <div style="margin-top:16px;">${chart}</div>
    </div>

    <div class="adm2-panel" style="margin-top:18px;padding:0;overflow:hidden;">
      <div style="padding:18px 20px;border-bottom:1px solid var(--a-border);">
        <div class="adm2-panel-title">Top referrers</div>
        <div class="adm2-panel-sub">all-time · click a row to see everyone this user has referred · sort by any column</div>
      </div>
      <div id="adm2-referrers-list" style="padding:8px 4px;"></div>
    </div>
  `;
}
function _mountCharts(host, d) {
  const chartHost = host.querySelector('#adm2-friends-chart');
  const series = d.timeseries || [];
  mountLineChart(chartHost, {
    series: [
      {
        name: 'Referrals joined',
        color: '#7C53E8',
        values: series.map((r) => Number(r.referrals_joined || 0)),
      },
      {
        name: 'Friendships accepted',
        color: '#0E7C5A',
        values: series.map((r) => Number(r.friendships_accepted || 0)),
      },
    ],
    xLabels: series.map((r) => r.date),
    height: 230,
    emptyMessage: 'No referral activity in this window.',
  });
}

function _mountReferrersTable(host, ctx, payload) {
  const target = host.querySelector('#adm2-referrers-list');
  if (!target) return;
  const items = (payload && payload.items) || [];
  if (!items.length) {
    target.innerHTML =
      '<div style="padding:32px 16px;text-align:center;color:var(--a-faint);">No referrals recorded yet.</div>';
    return;
  }
  mountSortableTable(target, {
    columns: [
      {
        key: 'user',
        label: 'User',
        text: true,
        get: (r) => (r.email || '').toLowerCase(),
        cell: (r) => `
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
            <span style="font:600 13px 'IBM Plex Sans',sans-serif;color:var(--a-ink);">${escape(r.username || (r.email || '').split('@')[0] || 'user')}</span>
            ${r.is_admin ? '<span class="adm2-chip" style="color:var(--a-brand);background:var(--a-brand-soft);border-color:transparent;">ADMIN</span>' : ''}
          </div>
          <div style="font:400 11px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-top:3px;">${escape(r.email || '')}</div>
        `,
      },
      {
        key: 'total',
        label: 'Referrals',
        right: true,
        get: (r) => Number(r.total || 0),
        cell: (r) => escape(fmtInt(r.total)),
        className: 'mono',
      },
      {
        key: 'rewarded',
        label: 'Rewarded',
        right: true,
        get: (r) => Number(r.rewarded || 0),
        cell: (r) => `<span style="color:var(--a-ok);">${escape(fmtInt(r.rewarded))}</span>`,
        className: 'mono',
      },
      {
        key: 'first_referred_at',
        label: 'First',
        right: true,
        get: (r) => (r.first_referred_at ? new Date(r.first_referred_at).getTime() : 0),
        cell: (r) => escape(fmtRelTime(r.first_referred_at)),
      },
      {
        key: 'last_referred_at',
        label: 'Last',
        right: true,
        get: (r) => (r.last_referred_at ? new Date(r.last_referred_at).getTime() : 0),
        cell: (r) => escape(fmtRelTime(r.last_referred_at)),
      },
    ],
    rows: items,
    defaultSort: { key: 'total', dir: 'desc' },
    minWidth: 780,
    onRowClick: (row) => ctx.navigate(`/admin/v2/friends/${row.user_id}`),
    pageSizeKey: 'referrers.list',
    emptyMessage: 'No referrers to show.',
  });
}
